import React, { useEffect, useRef, useState } from 'react';
import { CheckCircle, Users, BarChart3, Zap } from "lucide-react";
import { Swiper, SwiperSlide } from 'swiper/react';
import { EffectCards, Keyboard } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/effect-cards';
import './VerticalCardSlider.css';

const VerticalCardScroll = () => {
  const swiperRef = useRef(null);
  const containerRef = useRef(null); // фактичний DOM .mySwiper
  const [activeIndex, setActiveIndex] = useState(0);

  // ---- стабільний wheel-хендлер ----
  const accumRef = useRef(0);
  const cooldownRef = useRef(false);
  const lastTriggerRef = useRef(0);

  useEffect(() => {
    const el = containerRef.current || document.querySelector('.mySwiper');
    if (!el) return;

    const THRESHOLD_DELTA = 40;
    const THRESHOLD_TIME  = 250;

    const handleWheel = (e) => {
      const s = swiperRef.current;
      if (!s) return;
      const delta = e.deltaY;

      if (s.animating || cooldownRef.current) {
        e.preventDefault();
        return;
      }

      // на краях — віддаємо сторінці
      if ((s.isBeginning && delta < 0) || (s.isEnd && delta > 0)) {
        accumRef.current = 0;
        return;
      }

      e.preventDefault();
      accumRef.current += delta;

      const now = Date.now();
      if (Math.abs(accumRef.current) >= THRESHOLD_DELTA && (now - lastTriggerRef.current) >= THRESHOLD_TIME) {
        if (accumRef.current > 0) s.slideNext();
        else s.slidePrev();

        accumRef.current = 0;
        lastTriggerRef.current = now;

        cooldownRef.current = true;
        const wait = (s.params?.speed ?? 650) + 120;
        setTimeout(() => { cooldownRef.current = false; }, wait);
      }
    };

    el.addEventListener('wheel', handleWheel, { passive: false });
    return () => el.removeEventListener('wheel', handleWheel);
  }, []);

  // ---- наші власні touch-свайпи ----
  const tStartY = useRef(0);
  const tStartX = useRef(0);
  const lastY = useRef(0);            // для інкрементального скролу сторінки
  const didSlideThisGesture = useRef(false);
  const edgeScrollMode = useRef(false); // true коли на краю і скролимо сторінку вручну
  const lockDuringAnim = useRef(false);

  // налаштування підсилення краєвого скролу
  const SCROLL_GAIN = 20;     // ← зроби 1.2–2.0 для «більше руху за той самий жест»
  const STEP_DEADZONE = 1;   // дрібні тремтіння ігноруємо (в px)
  const STEP_CLAMP = 100;       // верхня межа одного кроку, щоб не «злітало»

  useEffect(() => {
    const el = containerRef.current || document.querySelector('.mySwiper');
    if (!el) return;

    const SWIPE_THRESHOLD = 18; // мінімальна дистанція по Y, щоб вважати свайпом
    const ANGLE_GUARD = 1.25;   // вертикаль має бути помітно сильнішою за горизонталь

    const onTouchStart = (e) => {
      const t = e.touches?.[0];
      if (!t) return;
      // перехоплюємо (зберігаємо стабільну модель, як у твоєму варіанті)
      if (e.cancelable) e.preventDefault();

      tStartY.current = t.clientY;
      tStartX.current = t.clientX;
      lastY.current = t.clientY;

      didSlideThisGesture.current = false;
      edgeScrollMode.current = false;
      // JS керує поведінкою — відключаємо нативний pan у контейнері
      el.style.touchAction = 'none';
    };

    const onTouchMove = (e) => {
      const s = swiperRef.current;
      if (!s) return;
      const t = e.touches?.[0];
      if (!t) return;

      // повністю перехоплюємо подію
      if (e.cancelable) e.preventDefault();

      // якщо вже йде анімація слайду — ігноруємо
      if (s.animating || lockDuringAnim.current) return;

      const dyTotal = t.clientY - tStartY.current;  // >0 — палець вниз (prev)
      const dxTotal = t.clientX - tStartX.current;

      // перевірка «це точно вертикальний свайп»
      if (Math.abs(dyTotal) <= Math.abs(dxTotal) * ANGLE_GUARD) {
        return;
      }

      // недостатньо сильний рух — нічого не робимо ще
      if (Math.abs(dyTotal) < SWIPE_THRESHOLD && !edgeScrollMode.current) return;

      // Вектор жесту → бажаний напрямок слайда
      const wantPrev = dyTotal > 0;  // вниз → prev
      const wantNext = dyTotal < 0;  // вгору → next

      const atBeginning = s.isBeginning;
      const atEnd = s.isEnd;

      // Якщо вже переключили слайд у цьому жесті — більше нічого не робимо
      if (didSlideThisGesture.current) return;

      // Чи можна крутити слайд у цьому напрямку?
      const canPrev = !atBeginning && wantPrev;
      const canNext = !atEnd && wantNext;

      if (canPrev || canNext) {
        // Перемикаємо рівно один раз за жест
        if (canNext) s.slideNext();
        else s.slidePrev();

        didSlideThisGesture.current = true;
        lockDuringAnim.current = true;
        const wait = (s.params?.speed ?? 650) + 120;
        setTimeout(() => { lockDuringAnim.current = false; }, wait);
        return;
      }

      // ===== КРАЙ: прокручуємо сторінку ПРОПОРЦІЙНО і ШВИДШЕ =====
      edgeScrollMode.current = true;

      // інкрементальний крок з підсиленням
      let dyStep = t.clientY - lastY.current;
      lastY.current = t.clientY;

      // мертва зона
      if (Math.abs(dyStep) < STEP_DEADZONE) return;

      // підсилюємо і обмежуємо
      dyStep = Math.max(-STEP_CLAMP, Math.min(STEP_CLAMP, dyStep * SCROLL_GAIN));

      // На мобі: тягнемо палець вгору (dyStep < 0) → контент має їхати вгору (scrollBy +)
      window.scrollBy({ top: -dyStep, behavior: 'auto' });
    };

    const onTouchEnd = () => {
      // жест завершився — повертаємо можливість нативного pan поза слайдером
      el.style.touchAction = '';
      edgeScrollMode.current = false;
      didSlideThisGesture.current = false;
    };

    // важливо: touchmove з passive:false
    el.addEventListener('touchstart', onTouchStart, { passive: false });
    el.addEventListener('touchmove', onTouchMove, { passive: false });
    el.addEventListener('touchend', onTouchEnd, { passive: true });

    return () => {
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchmove', onTouchMove);
      el.removeEventListener('touchend', onTouchEnd);
    };
  }, []);

  const totalSlides = 4;

  return (
    <div className="vertical-slider" style={{ background: '#fff', margin: '70px 0 100px 0', position: 'relative' }}>
      <div className="vertical-slider-container" style={{ display: 'flex', alignItems: 'center', maxWidth: '1200px', margin: '0 auto' }}>
        
        <div style={{ flex: 1, height: '100%' }}>
          <Swiper
            direction="vertical"
            effect="cards"
            cardsEffect={{ slideShadows: false, perSlideRotate: 0, perSlideOffset: 10 }}
            modules={[EffectCards, Keyboard]}
            onSwiper={(s) => {
              swiperRef.current = s;
              // зберігаємо DOM-елемент контейнера
              setTimeout(() => {
                if (s?.el) containerRef.current = s.el;
              }, 0);
            }}
            onSlideChange={(s) => setActiveIndex(s.activeIndex)}
            speed={650}
            allowTouchMove={false}  // усі touch-жести обробляємо самі
            simulateTouch={false}
            keyboard={{ enabled: true }}
            className="mySwiper"
            style={{ height: '100%' }}
          >

            {/* Slide 1 */}
            <SwiperSlide className='s-slide'>
              <div className="vertical-slide">
                <div className="slide-content">
                  <div className='slide-top'>
                    <div className="slide-icon"><CheckCircle /></div>
                    <p className="slide-step">Крок 1</p>
                  </div>
                  <p className="slide-title">Розкладіть цілі на Objectives, Key Results та ініціативи</p>
                  <p className="slide-description">Структуруйте стратегію у зрозумілому форматі OKR для максимальної ефективності</p>
                </div>
                <img src='/images/step-1.png' alt="Slide 1" />
              </div>
            </SwiperSlide>

            {/* Slide 2 */}
            <SwiperSlide className='s-slide'>
              <div className="vertical-slide">
                <div className="slide-content">
                  <div className='slide-top'>
                    <div className="slide-icon"><Users /></div>
                    <p className="slide-step">Крок 2</p>
                  </div>
                  <p className="slide-title">Встановіть дедлайн, пріоритетність і призначте відповідальних</p>
                  <p className="slide-description">Кожна ціль має відповідальну особу, терміни виконання та чіткий рівень пріоритету</p>
                </div>
                <img src='/images/step-2.png' alt="Slide 2" />
              </div>
            </SwiperSlide>

            {/* Slide 3 */}
            <SwiperSlide className='s-slide'>
              <div className="vertical-slide">
                <div className="slide-content">
                  <div className='slide-top'>
                    <div className="slide-icon"><BarChart3 /></div>
                    <p className="slide-step">Крок 3</p>
                  </div>
                  <p className="slide-title">Регулярно проводьте OKR-ревʼю, щоб слідкувати за досягненнями</p>
                  <p className="slide-description">Проводьте рев’ю з відповідальними, оновлюйте показники в реальному часі та оптимізуйте тактику і стратегію</p>
                </div>
                <img src='/images/step-3.png' alt="Slide 3" />
              </div>
            </SwiperSlide>

            {/* Slide 4 */}
            <SwiperSlide className='s-slide'>
              <div className="vertical-slide">
                <div className="slide-content">
                  <div className='slide-top'>
                    <div className="slide-icon"><Zap /></div>
                    <p className="slide-step">Крок 4</p>
                  </div>
                  <p className="slide-title">Платформа автоматично обрахує поточний прогрес виконання</p>
                  <p className="slide-description">Миттєво бачте відсоток виконання кожної цілі та ключового результату без ручних розрахунків</p>
                </div>
                <img src='/images/step-4.png' alt="Slide 4" />
              </div>
            </SwiperSlide>

          </Swiper>
        </div>

        <div className='dots'>
          {[...Array(totalSlides)].map((_, index) => (
            <button
              key={index}
              onClick={() => swiperRef.current?.slideTo(index)}
              style={{
                width: '10px',
                height: '10px',
                borderRadius: '50%',
                background: activeIndex === index ? '#1a1a1a' : '#999',
                border: 'none',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                transform: activeIndex === index ? 'scale(1.3)' : 'scale(1)',
              }}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default VerticalCardScroll;
